import { API, CurrencyType, EconomicsAPI, Endpoints } from '@brg/api';
import {EntityPaymentTypes} from "../constants/payment";
import axios from "axios";
import {DeliveryRequest} from "../constants/DeliveryRequest";

const economicsUrl = Endpoints.economics;

export default function economicUrlApi() {
    return {
        getOrder(orderId: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}`)
                .sendJson()
        },
        getProductsFields(params: any) {
            return axios.get(`${economicsUrl}/api/real-products/get`, { params });
        },
        getSequenceInfo(params: any) {
            return EconomicsAPI.GetSequenceInfo(params);
        },
        calculateFromCustomerPrice(form: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/real-products/calculate-from-customer-price`)
                .body(form)
                .sendJson()
        },
        calculateFromSellerPrice(form: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/real-products/calculate-from-seller-price`)
                .body(form)
                .sendJson()
        },
        getDeliveryTypes() {
            return axios.get(`${economicsUrl}/api/orders/delivery/types`);
        },

        getUserCurrencyAccounts(userId: string, currencyType?: number) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url("/currency-accounts")
                .addQuery("userId", userId)
                .addQuery("currencyType", currencyType ?? "")
                .addQuery("limit", 1000)
                .sendJson()
        },
        
        getUserCurrencyAccountsV2() {
            return API.request().endpoint(economicsUrl)
                .url(`/api/currency-accounts/v2/`)
                .sendJson();
        },

        getCurrencies() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/currencies`)
                .sendJson()
        },

        getProductTypes() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/products`)
                .sendJson()
        },

        calculatePrice(cardId: string, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card, currencyType: CurrencyType|number) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/calculate-price`)
                .body({
                    entity: {
                        type: paymentEntityType,
                        id: cardId
                    },
                    currencyType,
                })
                .sendJson()
        },

        getPrices(id: string, type: EntityPaymentTypes = EntityPaymentTypes.card) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/get-prices`)
                .body({ type, id })
                .sendJson()
        },
        
        getProductCardPricesV2({ id }: {id: string}) {
                    return API.request().endpoint(economicsUrl)
                        .url(`/api/prices/productcard/v2/${id}`)
                        .sendJson();
                },
        getPrintPrices(data: {cardId?: string, templateGroupId?: string}) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/card/print`)
                .addQuery("cardId", data.cardId || "")
                .addQuery("templateGroupId", data.templateGroupId || "")
                .sendJson()
        },

        getCalendarPrices(data: {cardId?: string}) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/calendar`)
                .addQuery("cardId", data.cardId || "")
                .sendJson()
        },

        getBookPricesInfo(id: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/book`)
                .addQuery("bookCardId", id || "")
                .sendJson()
        },

        
        getBookPrices(id: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/book/v2/${id}`)
                .sendJson()
        },

        createAndPurchaseOrder(id: string, currencyType: CurrencyType, params: any) {
            const paymentEntityType: EntityPaymentTypes = params.paymentEntityType || EntityPaymentTypes.card
            const requestBody = {
                products: [
                    {
                        type: paymentEntityType,
                        id,
                        cardId: id,
                        ...(params.productPrice ? { productPrice: params.productPrice } : {}),
                        ...params.tariff && { tariff: params.tariff },
                        ...params.bookId && { id: params.bookId },
                    }
                ],
                ...(params.totalPrice ? { totalPrice: params.totalPrice } : {}),
                ...params.options,
            };

            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/purchase`)
                .body(requestBody)
                .sendJson()
                .then(r => {
                    if (r?.code === 500) {
                        throw new Error(r?.message)
                    }
                    return r
                })
        },

        createOrder(id: string, currencyType: CurrencyType, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card, options?: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders`)
                .body({
                    products: [
                        {
                            type: paymentEntityType,
                            id
                        }
                    ],
                    currencyType,
                    ...options
                })
                .sendJson()
        },

        createOrderWithManyProducts(products: {id: string, type: EntityPaymentTypes}[], currencyType: CurrencyType) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders`)
                .body({
                    products: products,
                    currencyType
                })
                .sendJson()
        },

        addProduct(orderId: string, productId: string, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/products/add`)
                .body([{
                    type: paymentEntityType,
                    id: productId
                }])
                .sendJson()
        },

        getBundles() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/bundles`)
                .sendJson()
        },

        addDeliveryInfo(data: DeliveryRequest, orderId: string) {
            const date = new Date()

            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/delivery`)
                .body({
                    type: 1,
                    deliveryDate: {
                        customerDateTime: date.toISOString(),
                        actualDateTime: date.toISOString(),
                    },
                    address: {
                        fullAddress: data.countryRegionCity + data.streetHouseApartmentZipcode,
                        countryRegionCity: data.countryRegionCity,
                        streetHouseApartmentZipcode: data.streetHouseApartmentZipcode
                    },
                    contact: {
                        fullName: data.fullName,
                        email: data.email,
                        phoneNumber: data.phoneNumber
                    },
                    isComplete: false
                })
                .sendJson()
        },

        purchaseOrder(orderId: string) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/purchase`)
                .body({id: orderId})
                .sendJson()
        },

        getAvailableCurrencies(products: {id: string, type: EntityPaymentTypes}[]) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/products/available-purchase-type`)
                .body(products)
                .sendJson()
        },

        getAvailableTariffes(params: any) {
            return axios.get(`${economicsUrl}/api/sequences/tariffes`, { params });
        },

        getBookTariffes(params: any) {
            return axios.get(`${economicsUrl}/api/prices/book/tariffes`, { params });
        },

        /**
         * получение цены карточки с бека при вводе пользовательской цены карточки
         */
        calculateCustomPrise(params: any) {
            return axios.get(`${economicsUrl}/api/sequences/tariffes/custom`, { params });
        },

        getCurrenciesRate(from: string, to: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/rates`)
                .addQuery("from", from)
                .addQuery("to", to)
                .sendJson()
        },
    };
}
import { API, CurrencyType, EconomicsAPI, Endpoints } from '@brg/api';
import {EntityPaymentTypes} from "../constants/payment";
import axios from "axios";
import {DeliveryRequest} from "../constants/DeliveryRequest";

const economicsUrl = Endpoints.economics;

export default function economicUrlApi() {
    return {
        getOrder(orderId: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}`)
                .sendJson()
        },
        getProductsFields(params: any) {
            return axios.get(`${economicsUrl}/api/real-products/get`, { params });
        },
        getSequenceInfo(params: any) {
            return EconomicsAPI.GetSequenceInfo(params);
        },
        calculateFromCustomerPrice(form: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/real-products/calculate-from-customer-price`)
                .body(form)
                .sendJson()
        },
        calculateFromSellerPrice(form: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/real-products/calculate-from-seller-price`)
                .body(form)
                .sendJson()
        },
        getDeliveryTypes() {
            return axios.get(`${economicsUrl}/api/orders/delivery/types`);
        },

        getUserCurrencyAccounts(userId: string, currencyType?: number) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url("/currency-accounts")
                .addQuery("userId", userId)
                .addQuery("currencyType", currencyType ?? "")
                .addQuery("limit", 1000)
                .sendJson()
        },
        
        getUserCurrencyAccountsV2() {
            return API.request().endpoint(economicsUrl)
                .url(`/api/currency-accounts/v2/`)
                .sendJson();
        },

        getCurrencies() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/currencies`)
                .sendJson()
        },

        getProductTypes() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/products`)
                .sendJson()
        },

        calculatePrice(cardId: string, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card, currencyType: CurrencyType|number) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/calculate-price`)
                .body({
                    entity: {
                        type: paymentEntityType,
                        id: cardId
                    },
                    currencyType,
                })
                .sendJson()
        },

        getPrices(id: string, type: EntityPaymentTypes = EntityPaymentTypes.card) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/get-prices`)
                .body({ type, id })
                .sendJson()
        },
        
        getProductCardPricesV2({ id }: {id: string}) {
                    return API.request().endpoint(economicsUrl)
                        .url(`/api/prices/productcard/v2/${id}`)
                        .sendJson();
                },
        getPrintPrices(data: {cardId?: string, templateGroupId?: string}) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/card/print`)
                .addQuery("cardId", data.cardId || "")
                .addQuery("templateGroupId", data.templateGroupId || "")
                .sendJson()
        },

        getCalendarPrices(data: {cardId?: string}) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/calendar`)
                .addQuery("cardId", data.cardId || "")
                .sendJson()
        },

        getBookPricesInfo(id: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/book`)
                .addQuery("bookCardId", id || "")
                .sendJson()
        },

        
        getBookPrices(id: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/prices/book/v2/${id}`)
                .sendJson()
        },

        createAndPurchaseOrder(id: string, currencyType: CurrencyType, params: any) {
            const paymentEntityType: EntityPaymentTypes = params.paymentEntityType || EntityPaymentTypes.card
            const requestBody = {
                products: [
                    {
                        type: paymentEntityType,
                        id,
                        cardId: id,
                        ...(params.productPrice ? { productPrice: params.productPrice } : {}),
                        ...params.tariff && { tariff: params.tariff },
                        ...params.bookId && { id: params.bookId },
                    }
                ],
                ...(params.totalPrice ? { totalPrice: params.totalPrice } : {}),
                ...params.options,
            };

            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/purchase`)
                .body(requestBody)
                .sendJson()
                .then(r => {
                    if (r?.code === 500) {
                        throw new Error(r?.message)
                    }
                    return r
                })
        },

        createOrder(id: string, currencyType: CurrencyType, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card, options?: any) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders`)
                .body({
                    products: [
                        {
                            type: paymentEntityType,
                            id
                        }
                    ],
                    currencyType,
                    ...options
                })
                .sendJson()
        },

        createOrderWithManyProducts(products: {id: string, type: EntityPaymentTypes}[], currencyType: CurrencyType) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders`)
                .body({
                    products: products,
                    currencyType
                })
                .sendJson()
        },

        addProduct(orderId: string, productId: string, paymentEntityType: EntityPaymentTypes = EntityPaymentTypes.card) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/products/add`)
                .body([{
                    type: paymentEntityType,
                    id: productId
                }])
                .sendJson()
        },

        getBundles() {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/bundles`)
                .sendJson()
        },

        addDeliveryInfo(data: DeliveryRequest, orderId: string) {
            const date = new Date()

            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/delivery`)
                .body({
                    type: 1,
                    deliveryDate: {
                        customerDateTime: date.toISOString(),
                        actualDateTime: date.toISOString(),
                    },
                    address: {
                        fullAddress: data.countryRegionCity + data.streetHouseApartmentZipcode,
                        countryRegionCity: data.countryRegionCity,
                        streetHouseApartmentZipcode: data.streetHouseApartmentZipcode
                    },
                    contact: {
                        fullName: data.fullName,
                        email: data.email,
                        phoneNumber: data.phoneNumber
                    },
                    isComplete: false
                })
                .sendJson()
        },

        purchaseOrder(orderId: string) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/orders/${orderId}/purchase`)
                .body({id: orderId})
                .sendJson()
        },

        getAvailableCurrencies(products: {id: string, type: EntityPaymentTypes}[]) {
            return API.request()
                .post()
                .endpoint(`${economicsUrl}/api`)
                .url(`/products/available-purchase-type`)
                .body(products)
                .sendJson()
        },

        getAvailableTariffes(params: any) {
            return axios.get(`${economicsUrl}/api/sequences/tariffes`, { params });
        },

        getBookTariffes(params: any) {
            return axios.get(`${economicsUrl}/api/prices/book/tariffes`, { params });
        },

        /**
         * получение цены карточки с бека при вводе пользовательской цены карточки
         */
        calculateCustomPrise(params: any) {
            return axios.get(`${economicsUrl}/api/sequences/tariffes/custom`, { params });
        },

        getCurrenciesRate(from: string, to: string) {
            return API.request()
                .get()
                .endpoint(`${economicsUrl}/api`)
                .url(`/rates`)
                .addQuery("from", from)
                .addQuery("to", to)
                .sendJson()
        },
    };
}
